'use client';
import type { User } from '@v1/shared';
import { parsePermissions } from '@v1/shared';
import { type ReactNode, createContext, useContext, useMemo } from 'react';
import { useSession } from '../../lib/auth-client.js';

interface AuthContextValue {
	user: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue>({
	user: null,
	isAuthenticated: false,
	isLoading: false,
});

export function AuthProvider({
	user: serverUser,
	children,
}: {
	user: User | null;
	children: ReactNode;
}) {
	const { data: session, isPending } = useSession();

	const user = useMemo<User | null>(() => {
		if (typeof window === 'undefined' || isPending) return serverUser;
		if (session?.user) {
			const raw = session.user as Record<string, unknown>;
			return {
				id: session.user.id,
				email: session.user.email,
				name: session.user.name,
				emailVerified: session.user.emailVerified ?? false,
				image: session.user.image ?? null,
				permissions: parsePermissions(raw.permissions),
				twoFactorEnabled: (raw.twoFactorEnabled as boolean) ?? false,
			};
		}
		return null;
	}, [serverUser, session, isPending]);

	const isLoading = isPending && user === null;

	return <AuthContext value={{ user, isAuthenticated: !!user, isLoading }}>{children}</AuthContext>;
}

export function useAuth() {
	return useContext(AuthContext);
}
